import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/db70casjfl1ikpv8spm0';

const features = [
  {
    icon: 'ri-stack-line',
    title: '资源丰富',
    desc: '覆盖电影、电视剧、综艺三大频道，持续收录最新上映与热播内容。',
  },
  {
    icon: 'ri-equalizer-line',
    title: '多维筛选',
    desc: '按类型、地区、年份自由组合筛选，快速锁定你想看的那一类作品。',
  },
  {
    icon: 'ri-search-eye-line',
    title: '便捷搜索',
    desc: '支持影视名称、演员、类型等关键词模糊匹配，一步直达目标内容。',
  },
  {
    icon: 'ri-device-line',
    title: '多端适配',
    desc: '电脑、平板、手机皆可流畅浏览，界面清爽、信息层级清晰。',
  },
];

const values = [
  { value: '3', label: '内容频道' },
  { value: '20+', label: '精选影视' },
  { value: '每日', label: '持续更新' },
];

export default function About() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useSeo({
    title: '关于我们 - 看片影视大全',
    description:
      '了解看片影视大全的网站定位、服务范围与特色。我们致力于为影视爱好者提供清晰、丰富、易用的影视内容导航体验。',
    keywords: '关于看片,看片影视大全,影视平台介绍,联系我们',
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    setSubmitting(true);
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string' && value.trim() !== '') {
        payload.append(key, value);
      }
    });

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';

      if (response.ok && code === 'OK') {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(String(serverMsg));
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 text-sm rounded-md bg-background-50 border border-background-300/70 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all';

  return (
    <div className="w-full">
      <section className="bg-primary-950 text-background-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 md:py-20">
          <nav className="flex items-center gap-2 text-sm text-background-100/70 mb-5">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">关于我们</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">关于看片影视大全</h1>
          <p className="max-w-2xl text-sm md:text-base leading-relaxed text-background-100/85">
            看片是一个专注于影视内容展示与导航的平台，围绕最新电影、热门电视剧与精彩综艺节目，
            为影视爱好者提供清晰的结构、丰富的资源与便捷的浏览体验。
          </p>
          <div className="grid grid-cols-3 gap-4 md:gap-6 mt-10 max-w-2xl">
            {values.map((v) => (
              <div key={v.label} className="rounded-lg bg-background-50/10 border border-background-50/15 p-4 md:p-5">
                <p className="text-xl md:text-3xl font-extrabold text-accent-400">{v.value}</p>
                <p className="mt-1 text-xs md:text-sm text-background-100/80">{v.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight mb-4">网站定位</h2>
            <p className="text-sm md:text-base leading-loose text-foreground-700 mb-4">
              我们希望成为你在茫茫片海中值得信赖的“选片向导”。无论你是想追赶院线新片、追更正在热播的电视剧，
              还是想找一档轻松的综艺节目，看片都能帮你快速找到感兴趣的内容，并清晰了解每一部作品的类型、年份与简介。
            </p>
            <p className="text-sm md:text-base leading-loose text-foreground-700">
              需要特别说明的是，本站所有影视内容均为信息展示用途，不提供真实播放、会员或下载功能，
              站内数据以静态模拟方式呈现，仅用于内容浏览与介绍。
            </p>
          </div>
          <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
            <h2 className="text-xl font-bold text-foreground-950 mb-5">服务范围与特色</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {features.map((f) => (
                <div key={f.title} className="flex gap-3">
                  <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${f.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{f.title}</h3>
                    <p className="text-xs leading-relaxed text-foreground-500">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight mb-4">联系我们</h2>
              <p className="text-sm md:text-base leading-relaxed text-foreground-700 mb-6">
                如果你对网站有任何建议、内容反馈或合作意向，欢迎通过下方留言表单与我们联系。
                我们会认真阅读每一条反馈，让看片变得更好。
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 border border-background-200 text-primary-600">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">反馈邮箱</p>
                    <p className="text-sm text-foreground-900">feedback@kanpian.example</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 border border-background-200 text-primary-600">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">反馈处理</p>
                    <p className="text-sm text-foreground-900">工作日 1-3 天内回复</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-background-50 border border-background-200/70 p-6 md:p-8">
              <h3 className="text-lg font-bold text-foreground-950 mb-5">在线留言</h3>
              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-checkbox-circle-line text-3xl"></i>
                  </div>
                  <p className="text-base font-semibold text-foreground-950">反馈已提交，感谢你的留言！</p>
                  <p className="mt-2 text-sm text-foreground-500">我们会尽快查看并与你联系。</p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-5 px-5 py-2 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form data-readdy-form id="kanpian-contact-form" onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="kp-name" className="block text-xs font-medium text-foreground-600 mb-1.5">
                      你的称呼
                    </label>
                    <input id="kp-name" name="name" required placeholder="请输入称呼" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="kp-email" className="block text-xs font-medium text-foreground-600 mb-1.5">
                      联系邮箱
                    </label>
                    <input id="kp-email" name="email" type="email" required placeholder="请输入邮箱" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="kp-message" className="block text-xs font-medium text-foreground-600 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="kp-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请输入你的建议或反馈（最多 500 字）"
                      className={`${inputClass} resize-none`}
                    ></textarea>
                  </div>

                  <input
                    className="kanpian-extra-field"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === 'error' && (
                    <p className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? '提交中…' : '提交反馈'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}